<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">{{ $t('CMS-Templates') }}</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('admin_cms')"
                                :key="badge.key"
                                :title="badge.title || $t('Shortcut')"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" to="/workspace/cms">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-left"></svg-vue>
                    </router-link>
                    <router-link class="btn-stage-icon-plain" :title="$t('Template anlegen')" :aria-label="$t('Template anlegen')" to="/workspace/cms/templates/new">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                        <div v-if="templates.length > 0" class="hidden sm:block">
                            <div class="align-middle inline-block min-w-full border-b border-ultralight">
                                <table class="table-listview table-listview--zebra">
                                    <thead>
                                    <tr>
                                        <th class="table-listview-l-th">{{ $t('Template') }}</th>
                                        <th class="table-listview-l-th">{{ $t('Beschreibung') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-32">{{ $t('Seiten') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-32">{{ $t('Systemtemplate') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-48">{{ $t('Erstellt von') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-48">{{ $t('Letzte Veröffentlichung') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-40">{{ $t('Update') }}</th>
                                    </tr>
                                    </thead>
                                    <tbody class="bg-white">
                                    <tr
                                        v-for="template in templates"
                                        :key="template.id"
                                        class="cursor-pointer"
                                        @click="$router.push('/workspace/cms/templates/' + template.id + '/edit')"
                                    >
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div class="flex items-center min-w-0">
                                                <div class="mr-3 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-700">
                                                    <svg-vue class="w-8 h-8 p-1.5 text-gray-200" icon="font-awesome.regular.sidebar"></svg-vue>
                                                </div>
                                                <div class="min-w-0">
                                                    <div class="flex items-center gap-2 min-w-0">
                                                        <div class="text-sm font-medium text-gray-900 truncate">{{ template.name }}</div>
                                                        <span :class="templateStatusMeta(template).dotClass" class="h-2 w-2 rounded-full flex-shrink-0"></span>
                                                    </div>
                                                    <div v-if="template.is_system" class="text-xs text-gray-500 mt-1">
                                                        {{ $t('Basistemplate · nicht löschbar') }}
                                                    </div>
                                                    <div v-else-if="!template.published_at" class="text-xs text-gray-500 mt-1">
                                                        {{ $t('Noch nicht veröffentlicht') }}
                                                    </div>
                                                    <div v-else-if="template.has_unpublished_changes" class="text-xs text-amber-600 mt-1">
                                                        {{ $t('Unveröffentlichte Änderungen') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td">
                                            <div class="text-sm text-gray-700 truncate">{{ template.description || '-' }}</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-700">
                                            {{ template.pages_count }}
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div v-if="template.is_system" class="inline-flex items-center justify-center">
                                                <svg-vue class="h-5 w-3 text-gray-400" icon="font-awesome.regular.lock"></svg-vue>
                                            </div>
                                            <div v-else class="text-sm text-gray-500">-</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div class="flex items-center min-w-0 max-w-[12rem] text-sm text-gray-500">
                                                <img
                                                    v-if="createdActorAvatar(template)"
                                                    :src="createdActorAvatar(template)"
                                                    :alt="createdActorName(template)"
                                                    :title="createdActorName(template)"
                                                    class="h-6 w-6 rounded-full border border-white object-cover mr-2 flex-shrink-0"
                                                >
                                                <span class="truncate text-gray-900">{{ createdActorName(template) }}</span>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div v-if="template.published_by_user" class="flex items-center min-w-0 max-w-[12rem] text-sm text-gray-500">
                                                <img
                                                    :src="publishedActorAvatar(template)"
                                                    :alt="publishedActorName(template)"
                                                    :title="publishedActorName(template)"
                                                    class="h-6 w-6 rounded-full border border-white object-cover mr-2 flex-shrink-0"
                                                >
                                                <span class="truncate text-gray-900">{{ publishedActorName(template) }}</span>
                                            </div>
                                            <div v-else class="text-sm text-gray-500">-</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-500">
                                            {{ template.updated_at ? $momentFormatDateTimeAgo(template.updated_at) : '-' }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div v-else-if="!loading" class="h-full">
                            <empty-state />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    cmsCreatedActorAvatar,
    cmsCreatedActorName,
    cmsPublishedActorAvatar,
    cmsPublishedActorName,
} from '@/utils/cms-update-meta';
import {cmsPublicationStatusMeta} from '@/utils/cms-status-badge';

export default {
    name: "cms-templates-list",
    data() {
        return {
            loading: true,
            templates: [],
        };
    },
    mounted() {
        this.loadTemplates();
    },
    methods: {
        createdActorAvatar(template) {
            return cmsCreatedActorAvatar(template);
        },
        createdActorName(template) {
            return cmsCreatedActorName(template);
        },
        publishedActorAvatar(template) {
            return cmsPublishedActorAvatar(template);
        },
        publishedActorName(template) {
            return cmsPublishedActorName(template);
        },
        templateStatusMeta(template) {
            return cmsPublicationStatusMeta(this, template);
        },
        loadTemplates() {
            this.loading = true;
            axios.get('api/workspace/admin/cms/templates').then((response) => {
                this.templates = Array.isArray(response.data) ? response.data : [];
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
    },
}
</script>
